/**
 * Streak Indicator Component
 * Shows current streak with flame icon and optional calendar heatmap
 */

'use client';

import { useState } from 'react';
import { Flame, Calendar } from 'lucide-react';

interface StreakIndicatorProps {
  currentStreak: number;
  longestStreak: number;
  lastActivityDate?: string | null;
  streakActive?: boolean;
  activityHistory?: Record<string, number>;
  variant?: 'compact' | 'full';
  showCalendar?: boolean;
}

export default function StreakIndicator({
  currentStreak,
  longestStreak,
  lastActivityDate,
  streakActive = false,
  activityHistory = {},
  variant = 'compact',
  showCalendar = false,
}: StreakIndicatorProps) {
  const [showHeatmap, setShowHeatmap] = useState(false);

  // Generate last 30 days for calendar heatmap
  const getLast30Days = () => {
    const days = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      days.push({
        date: dateStr,
        dayOfWeek: date.getDay(),
        dayOfMonth: date.getDate(),
        count: activityHistory[dateStr] || 0,
      });
    }
    return days;
  };

  const getActivityLevel = (count: number): string => {
    if (count === 0) return 'bg-cream/10';
    if (count <= 2) return 'bg-accent/30';
    if (count <= 5) return 'bg-accent/60';
    return 'bg-accent';
  };

  if (variant === 'compact') {
    return (
      <div
        className="flex items-center gap-2 cursor-pointer group"
        onClick={() => showCalendar && setShowHeatmap(!showHeatmap)}
      >
        <div
          className={`
            flex items-center gap-1.5 px-3 py-1.5 rounded-full
            ${streakActive ? 'bg-accent-muted border border-border' : 'bg-cream/10 border border-border-muted'}
            transition-all group-hover:scale-105
          `}
        >
          <Flame
            className={`w-4 h-4 ${streakActive ? 'text-accent animate-pulse' : 'text-foreground-tertiary'}`}
          />
          <span className={`font-bold ${streakActive ? 'text-accent' : 'text-foreground-tertiary'}`}>
            {currentStreak}
          </span>
        </div>
        {showCalendar && (
          <Calendar className="w-4 h-4 text-foreground-tertiary group-hover:text-foreground transition-colors" />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Streak Stats */}
      <div className="flex items-center gap-6">
        {/* Current Streak */}
        <div className="flex items-center gap-3">
          <div
            className={`
              w-12 h-12 rounded-full flex items-center justify-center
              ${streakActive ? 'bg-accent-muted' : 'bg-cream/10'}
            `}
          >
            <Flame
              className={`w-6 h-6 ${streakActive ? 'text-accent' : 'text-foreground-tertiary'}`}
            />
          </div>
          <div>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-foreground">{currentStreak}</span>
              <span className="text-sm text-foreground-tertiary">day{currentStreak !== 1 ? 's' : ''}</span>
            </div>
            <p className="text-xs text-foreground-tertiary">Current streak</p>
          </div>
        </div>

        {/* Longest Streak */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-cream/10 flex items-center justify-center">
            <span className="text-xl">🏆</span>
          </div>
          <div>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-foreground">{longestStreak}</span>
              <span className="text-sm text-foreground-tertiary">day{longestStreak !== 1 ? 's' : ''}</span>
            </div>
            <p className="text-xs text-foreground-tertiary">Longest streak</p>
          </div>
        </div>
      </div>

      {/* Streak Status Message */}
      <div
        className={`
          p-3 rounded-lg border
          ${
            streakActive
              ? 'bg-green-500/10 border-green-500/30 text-green-400'
              : 'bg-accent-muted border-border text-accent'
          }
        `}
      >
        {streakActive ? (
          <p className="text-sm">
            🎉 Great job! You&apos;ve been active today. Keep it up!
          </p>
        ) : currentStreak > 0 ? (
          <p className="text-sm">
            ⚠️ Don&apos;t lose your {currentStreak}-day streak! Complete an activity today.
          </p>
        ) : (
          <p className="text-sm">
            🚀 Start a new streak by reading an article or using a tool today!
          </p>
        )}
      </div>

      {/* Activity Heatmap */}
      {showCalendar && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-foreground-secondary">Last 30 days</h4>
            <div className="flex items-center gap-1 text-xs text-foreground-tertiary">
              <span>Less</span>
              <div className="flex gap-0.5">
                <div className="w-3 h-3 rounded-sm bg-cream/10" />
                <div className="w-3 h-3 rounded-sm bg-accent/30" />
                <div className="w-3 h-3 rounded-sm bg-accent/60" />
                <div className="w-3 h-3 rounded-sm bg-accent" />
              </div>
              <span>More</span>
            </div>
          </div>
          <div className="grid grid-cols-10 gap-1">
            {getLast30Days().map((day) => (
              <div
                key={day.date}
                className={`
                  w-6 h-6 rounded-sm ${getActivityLevel(day.count)}
                  flex items-center justify-center
                  text-[10px] text-foreground-tertiary
                  hover:ring-1 hover:ring-cream/30
                  cursor-default
                `}
                title={`${day.date}: ${day.count} activities`}
              >
                {day.dayOfMonth}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
